import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useAuth } from "../../../../contexts/AuthProvider/useAuth";
import { formatDataCompare, getLocalStorage, isMobileDevice, setLocalStorage, toastError } from "../../../../commons/utils";
import { Grid, LinearProgress, Box } from "@mui/material";
import { ExSelect, ExSelectHandler } from "../../../../components/ExSelect";
import { model_tipo_movimento, model_usuario } from "../../../../models";
import { ExSelectFilter, ExSelectFilterHandler } from "../../../../components/ExSelectFilter";
import { Input } from "../../../../components/Input";
import moment from "moment";

const REFRESH_INTERVAL = 60000;

const DateInput = forwardRef(({ id, label, onChange, defaultValue }: any, ref) => (
    <Input ref={ref} id={id} type="date" label={label} onAfterChange={onChange} defaultValue={defaultValue} />
));

export const FiltroMovimentoKanban = forwardRef((props: { onGetData: (data: any, tipmovcontador: number) => void }, ref: any) => {
    const auth = useAuth();

    const reftipomov = useRef<ExSelectHandler>(null);
    const refemissaoi = useRef<any>(null);
    const refemissaof = useRef<any>(null);
    const refUsuario = useRef<ExSelectFilterHandler>(null);

    const [data, setData] = useState<any>();
    const [refreshProgress, setRefreshProgress] = useState<number>(0);
    const [autoRefreshActive, setAutoRefreshActive] = useState(false);
    const abaAtiva = useRef(true);

    const refreshTimer = useRef<NodeJS.Timeout | null>(null);
    const progressTimer = useRef<NodeJS.Timeout | null>(null);

    function refresh() {
        change(true);
    }

    useImperativeHandle(ref, () => ({ refresh }));

    useEffect(() => {
        auth.get('/v1/tipomovivmentowithstatus').then((response: any) => {
            const formattedData = response.data.map((item: any) => ({ value: item.tipmovcontador, label: item.tipmovdescricao + ' - ' + item.produtodesc }));
            reftipomov.current?.setCustomOptions(formattedData);
            if (formattedData.length === 1)
                reftipomov.current?.setValue(formattedData[0]);
        });

        function onVisibilityChange() {
            abaAtiva.current = !document.hidden;
        }
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            clearTimers();
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function clearTimers() {
        if (refreshTimer.current) clearInterval(refreshTimer.current);
        if (progressTimer.current) clearInterval(progressTimer.current);
        setRefreshProgress(0);
    }

    function startAutoRefresh(tipomov: any) {
        clearTimers();
        setAutoRefreshActive(true);
        setRefreshProgress(100);

        let remaining = 100;
        const step = 100 / (REFRESH_INTERVAL / 100);

        progressTimer.current = setInterval(() => {
            remaining -= step;
            if (remaining < 0) remaining = 0;
            setRefreshProgress(remaining);
        }, 100);

        refreshTimer.current = setInterval(() => {
            if (abaAtiva.current) {
                change(true, tipomov);
                remaining = 100;
                setRefreshProgress(100);
            }
        }, REFRESH_INTERVAL);
    }

    async function getDados(refresh: boolean, tiposmovimento?: any) {
        let _data: any;
        if (!refresh && data)
            _data = data;
        else {
            await auth.get('/v1/movimentoskanban')
                .then((response: any) => {
                    setData(response.data);
                    _data = response.data;
                })
                .catch((error: any) => {
                    toastError(error);
                });
        }

        let _tiposmovimento = tiposmovimento;
        if (!_tiposmovimento)
            _tiposmovimento = reftipomov.current?.getValue();

        if (_tiposmovimento && _data)
            _data = _data.filter((item: any) => item.tipmovcontador === _tiposmovimento.value);

        return _data;
    }

    const filterByDate = (data: any[], startDate?: any, endDate?: any) =>
        data.filter((item: any) => {
            const date = formatDataCompare(item.movemissao);
            return (!startDate || date >= startDate) && (!endDate || date <= endDate);
        });

    function change(refresh: boolean, tiposmovimento?: any) {
        getDados(refresh, tiposmovimento).then((data: any) => {
            data = filterByDate(data, refemissaoi.current?.getValue(), refemissaof.current?.getValue());
            data = refUsuario.current?.applyFilter(data) || data;

            if (props.onGetData && tiposmovimento)
                props.onGetData(data, tiposmovimento.value);

            setLocalStorage('iDataKanbanMovimento', refemissaoi.current?.getValue() ?? null);
            setLocalStorage('fDataKanbanMovimento', refemissaof.current?.getValue() ?? null);
    
            if (tiposmovimento)
                startAutoRefresh(tiposmovimento);
        });
    }

    function changeTipoMov(_newValue: any) {
        change(false, _newValue);
    }

    const changeSelector = () => {
        const tipmov = reftipomov.current?.getValue();
        if (tipmov) change(false, { value: tipmov });
    };

    return (
        <Grid item xs={12}>
            {autoRefreshActive && (
                <Box sx={{ width: '100%', mb: 2 }}>
                    <LinearProgress
                        variant="determinate"
                        value={refreshProgress}
                        sx={{
                            height: 2,
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: 'var(--primary-color)'
                            }
                        }}
                    />
                </Box>
            )}
            <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Grid item xs={isMobileDevice() ? 12 : 3}>
                    <ExSelect ref={reftipomov} id='tipomovimento' label='Tipo Movimento' model={model_tipo_movimento} onAfterChange={changeTipoMov} />
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Grid item xs={isMobileDevice() ? 12 : 3}>
                            <DateInput ref={refemissaoi} id="kbest_emis_inicial" label="Emissão Inicial" onChange={changeSelector} defaultValue={getLocalStorage('iDataKanbanMovimento') ? getLocalStorage('iDataKanbanMovimento') : moment().format('YYYY-01-01')} />
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 3}>
                            <DateInput ref={refemissaof} id="kbest_emis_final" label="Emissão Final" onChange={changeSelector} defaultValue={moment().format('YYYY-MM-DD')} />
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 6}>
                            <ExSelectFilter
                                ref={refUsuario}
                                model={model_usuario}
                                onAfterChange={changeSelector}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
});
